<script setup lang="ts">
  import FlowPathInput from '@/components/form/FlowPathInput.vue'
  import {
    DropDown,
    UiButton,
    type ComparisonOperator,
    type FlowPredicate,
    FormFieldContainer,
  } from '@logistiek-zonder-papier/lzp-library'

  const operators: { key: ComparisonOperator; symbol: string }[] = [
    { key: 'EQ', symbol: '=' },
    { key: 'NEQ', symbol: '!=' },
    { key: 'GT', symbol: '>' },
    { key: 'GTE', symbol: '>=' },
    { key: 'LT', symbol: '<' },
    { key: 'LTE', symbol: '<=' },
    { key: 'INCLUDES', symbol: 'incl.' },
  ]

  const predicates = defineModel<FlowPredicate[]>({ required: true })
  defineProps<{ context?: Record<string, unknown> | undefined }>()

  function addPredicate() {
    predicates.value ??= []
    predicates.value.push({
      id: undefined,
      leftHandOperator: '',
      operator: 'EQ',
      rightHandOperator: '',
      order: predicates.value.length,
    })
  }

  function removePredicate(index: number) {
    predicates.value.splice(index, 1)
    predicates.value.forEach((predicate: FlowPredicate, index: number) => (predicate.order = index))
  }
</script>

<template>
  <div>
    <FormFieldContainer label="Predicate">
      <template #action-button>
        <UiButton variant="prominent" icon="plus" @click="addPredicate" size="small" />
      </template>
      <div class="repeatable">
        <div v-for="(predicate, index) in predicates" :key="index" class="predicate-row">
          <FlowPathInput
            :optional="false"
            placeholder="item"
            v-model="predicate.leftHandOperator"
            type="text"
            :context="context"
            :validate-paths="true"
          />

          <DropDown
            :modelValue="operators.find(o => o.key === predicate.operator)"
            @update:modelValue="
              (value: { key: ComparisonOperator }) => (predicate.operator = value.key)
            "
            required
            class="operator-dropdown"
            :data="operators"
          >
            <template #item="{ item }">
              <div class="operators">{{ item.symbol }}</div>
            </template>
          </DropDown>

          <FlowPathInput
            :optional="false"
            placeholder="context"
            v-model="predicate.rightHandOperator"
            type="text"
            :validate-paths="true"
          />

          <UiButton
            class="delete-btn"
            icon="delete"
            size="small"
            variant="negative"
            @click="removePredicate(index)"
          />
        </div>
      </div>
    </FormFieldContainer>
  </div>
</template>

<style lang="css" scoped>
  :deep(.form-field-container) {
    margin-bottom: 0;
  }

  .operator-dropdown :deep(.selection-options) {
    width: 200%;
  }

  .operators {
    font-size: 1rem;
  }

  .repeatable {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .predicate-row {
    display: grid;
    grid-template-columns: 1fr 4rem 1fr auto;
    gap: 0.5rem;
    align-items: center;
    align-content: center;
  }

  .add-btn {
    align-self: flex-start;
    background: none;
    border: none;
    padding: 0.25rem 0;
    color: var(--colours-brand-colour-1);
    font-size: 0.875rem;
    cursor: pointer;
  }

  .remove-btn {
    width: 1.75rem;
    height: 1.75rem;
    border: 1px solid rgb(209, 213, 219);
    border-radius: 0.375rem;
    background: var(--colours-grey-1);
    color: rgb(107, 114, 128);
    cursor: pointer;
    line-height: 1;
  }

  .select {
    height: 2.25rem;
    padding: 0 0.5rem;
    border: 1px solid var(--colours-grey-2);
    border-radius: 0.3;
    background: var(--colours-grey-1);
    font-size: 0.875rem;
  }
</style>
